import { defineStore } from "pinia";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useLocalStorage } from "@vueuse/core";
import { useStyleStore } from "@vcl";
import { useUserPreferenceStore } from "@/stores/user-preference-store";
import useDynamicComponentStore from "%/dynamic-component-editor/stores/dynamic-component-store";
import {
  shouldHideComponentByFeature,
  shouldHideGroupByFeature,
  shouldHidePageByFeature,
} from "~/constants/feature-config";

export const LEFT_PANEL_TWO_MIN_WIDTH = 310;
export const LEFT_PANEL_MAX_WIDTH = 800;
export const RIGHT_PANEL_MIN_WIDTH = 385;

export const useStagingFrameStore = defineStore("staging-frame-store", {
  state: () => ({
    standardDynamicPageConfig: [],
    standardDynamicComponentConfig: [],
    collapsedStateForPageConfig: useLocalStorage("staging-frame-collapsed-state-for-page-config", {}),
    collapsedStateForComponentConfig: useLocalStorage("staging-frame-collapsed-state-for-component-config", {}),
    leftPanelTwoCollapsed: useLocalStorage("staging-frame-left-panel-two-collapsed", false),
    rightPanelCollapsed: useLocalStorage("staging-frame-right-panel-collapsed", false),
    screenVariant: useLocalStorage("staging-frame-screen-variant", "desktop"),
    appView: useLocalStorage("staging-frame-app-view", false),
    rightPanelWidth: useLocalStorage("staging-frame-right-panel-width", RIGHT_PANEL_MIN_WIDTH),
    internalComponentVariantToUse: useLocalStorage("internal-component-variant-to-use", {}),
    showPreviewModal: useLocalStorage("show-preview-modal", false),
    designMode: false,
    themeId: 0,
    elementsPinnedToPanel: "pages",
    showRightPanelSectionStateAnimations: false,
    dynamicComponentClassName: null,
    isLoadingPageChange: false,
    previewUrl: "",
    elementSearchTerm: "",
    hasDynamicComponent: true,

    hideInspectors: false,
    showEntityBuilder: false,
    showComponentPreview: false,
    frameMeta: null,

    showCanvasOverlay: false,
    hideLeftPanel: false,
    hideRightPanel: false,
    canvasOverlayContext: null,
    primaryContentSection: "",
    brandingAutoOpenedRightPanel: false,
    activeSection: "",
  }),
  getters: {
    standardDynamicComponentVariants() {
      return useDynamicComponentStore().dynamicComponentVariants;
    },
    customDynamicPages() {
      return useConfigurationStore().customDynamicPages;
    },
    customDynamicComponents() {
      return useConfigurationStore().customDynamicComponents;
    },
    themeVariant() {
      return useStyleStore().getThemeMode(this.themeId);
    },
    dynamicPagesConfigFlat() {
      return this.dynamicPagesConfigForUsers.reduce((pageList, group) => {
        return [...pageList, ...group.items];
      }, []);
    },
    dynamicComponentsConfigFlat() {
      return this.dynamicComponentsConfigForUsers.reduce((componentList, group) => {
        return [...componentList, ...group.items];
      }, []);
    },
    dynamicPagesConfigForUsers() {
      const configurationStore = useConfigurationStore();
      const remapped = this.standardDynamicPageConfig.map(groupData => {
        if (shouldHideGroupByFeature(groupData.group, configurationStore)) {
          return null;
        }

        const groupDataCopy = { ...groupData, items: [...groupData.items] };

        const classGroups = {};
        groupDataCopy.items.forEach(item => {
          const classKey = item["page-class"];
          if (!classGroups[classKey]) {
            classGroups[classKey] = [];
            const baseOrder = item.order;
            if (this.standardDynamicComponentVariants[classKey]) {
              const dynamicComponentVariantId = this.standardDynamicComponentVariants[classKey];
              const variantItem = groupDataCopy.items.find(component => {
                return component["page-id"] === dynamicComponentVariantId.dynamic_component_id;
              });
              if (!variantItem) {
                const missingConfig = useDynamicComponentStore().allDynamicComponentsFromHierarchy[dynamicComponentVariantId.dynamic_component_id];
                classGroups[classKey].push({
                  "display-name": missingConfig.rootOptions["display-name"] || missingConfig.name,
                  "page-class": missingConfig.class,
                  "page-id": missingConfig.dynamic_component_id,
                  "page-name": missingConfig.name,
                  "page-slug": missingConfig.rootOptions["page-slug"],
                  "order": baseOrder,
                });
              } else {
                classGroups[classKey].push({
                  ...variantItem,
                  "order": variantItem.order ?? baseOrder,
                });
              }
            } else {
              classGroups[classKey].push(item);
            }
          }
        });

        const transformedItems = [];
        Object.values(classGroups).forEach(items => {
          transformedItems.push(items[0]);
        });

        transformedItems.sort((a, b) => {
          return a.order - b.order;
        });

        const visibleItems = transformedItems.filter(item => !shouldHidePageByFeature(item, configurationStore));

        if (!visibleItems.length) {
          return null;
        }

        groupDataCopy.items = groupDataCopy.items.filter(item => item);

        return {
          ...groupDataCopy,
          items: visibleItems,
        };
      }).filter(Boolean);

      return [
        ...remapped,
        {
          "group": "Custom",
          "items": Object.values(this.customDynamicPages).filter(page => !shouldHidePageByFeature(page, configurationStore)),
        },
      ];
    },
    dynamicComponentsConfigForUsers() {
      const configurationStore = useConfigurationStore();

      const remapped = this.standardDynamicComponentConfig.map(groupData => {
        if (shouldHideGroupByFeature(groupData.group, configurationStore)) {
          return null;
        }

        const classGroups = {};

        groupData.items.forEach(item => {
          const classKey = item["component-class"];

          if (!classGroups[classKey]) {
            classGroups[classKey] = [];
            const baseOrder = item.order;

            if (this.standardDynamicComponentVariants[classKey]) {
              const dynamicComponentVariantId = this.standardDynamicComponentVariants[classKey];
              const variantItem = groupData.items.find(component => {
                return component["component-id"] === dynamicComponentVariantId.dynamic_component_id;
              });
              if (!variantItem) {
                const missingConfig = useDynamicComponentStore().allDynamicComponentsFromHierarchy[dynamicComponentVariantId.dynamic_component_id];
                classGroups[classKey].push({
                  "display-name": missingConfig.rootOptions["display-name"] || missingConfig.name,
                  "component-class": missingConfig.class,
                  "component-id": missingConfig.dynamic_component_id,
                  "component-name": missingConfig.name,
                  "order": baseOrder,
                });
              } else {
                classGroups[classKey].push({
                  ...variantItem,
                  "order": variantItem.order ?? baseOrder,
                });
              }
            } else {
              classGroups[classKey].push(item);
            }
          }
        });

        const transformedItems = [];

        Object.values(classGroups).forEach(items => {
          transformedItems.push(items[0]);
        });

        transformedItems.sort((a, b) => {
          return a.order - b.order;
        });

        const visibleItems = transformedItems.filter(item => !shouldHideComponentByFeature(item, configurationStore));

        if (!visibleItems.length) {
          return null;
        }

        groupData.items = groupData.items.filter(item => item);

        return {
          ...groupData,
          items: visibleItems,
        };
      }).filter(Boolean);

      return [
        ...remapped,
        {
          "group": "Custom",
          "items": Object.values(this.customDynamicComponents).filter(component => !shouldHideComponentByFeature(component, configurationStore)),
        },
      ];
    },
  },
  actions: {
    setPrimaryContentSection(section) {
      this.primaryContentSection = section;
    },
    toggleCanvasOverlay(component, props, hideLeftPanel = false, hideRightPanel = false) {
      if (!component) {
        this.showCanvasOverlay = false;
        this.hideLeftPanel = false;
        this.hideRightPanel = false;
        this.canvasOverlayContext = null;
        return;
      }
      this.showCanvasOverlay = true;
      this.hideLeftPanel = hideLeftPanel;
      this.hideRightPanel = hideRightPanel;
      this.canvasOverlayContext = {
        component: component,
        props: props ?? {},
      };
    },
    updatePageDisplayNameInStandardPageConfig(displayName, pageId) {
      this.standardDynamicPageConfig = this.standardDynamicPageConfig.map(group => {
        return {
          ...group,
          items: group.items.map(groupItem => {
            if (groupItem["page-id"] && groupItem["page-id"] === pageId) {
              return {
                ...groupItem,
                "display-name": displayName,
              };
            }
            return groupItem;
          }),
        };
      });
    },
    updatePageIdInStandardPageConfig(componentName, pageId) {
      this.standardDynamicPageConfig = this.standardDynamicPageConfig.map(group => {
        return {
          ...group,
          items: group.items.map(groupItem => {
            if (groupItem["page-name"] && groupItem["page-name"] === componentName) {
              return {
                ...groupItem,
                "page-id": pageId,
              };
            }
            return groupItem;
          }),
        };
      });
    },
    removeComponentFromPageConfig(componentId) {
      this.standardDynamicPageConfig = this.standardDynamicPageConfig.map(group => {
        return {
          ...group,
          items: group.items.filter(groupItem => {
            return groupItem["page-id"] !== componentId;
          }),
        };
      });
      delete useConfigurationStore().$_configuration.dynamicComponents[componentId];
    },
    removeComponentFromComponentConfig(componentId) {
      this.standardDynamicComponentConfig = this.standardDynamicComponentConfig.map(group => {
        return {
          ...group,
          items: group.items.filter(groupItem => {
            return groupItem["component-id"] !== componentId;
          }),
        };
      });
      delete useConfigurationStore().$_configuration.dynamicComponents[componentId];
    },
    addComponentToStandardComponentOrPageConfig(componentId, componentClass, componentName, displayName, pageSlug) {
      let updatedStagingFrame = false;

      this.standardDynamicPageConfig.forEach((group, index) => {
        if (!updatedStagingFrame && group.items.find(component => component["page-class"] === componentClass)) {

          this.standardDynamicPageConfig[index].items.push({
            "page-id": componentId,
            "page-slug": pageSlug,
            "page-class": componentClass,
            "page-name": componentName,
            "display-name": `${displayName}`,
          });
          updatedStagingFrame = true;
        }
      });

      if (!updatedStagingFrame) {
        this.standardDynamicComponentConfig.forEach((group, index) => {
          if (!updatedStagingFrame && group.items.find(component => component["component-class"] === componentClass)) {
            this.standardDynamicComponentConfig[index].items.push({
              "component-class": componentClass,
              "component-name": componentName,
              "component-id": componentId,
            });
          }
        });
      }
    },
    setThemeVariant(appId, themeMode) {
      const styleStore = useStyleStore();
      styleStore.setAppThemeMode(appId, themeMode, this.themeId);
      useUserPreferenceStore().updateUserPreferenceByName("appThemeModes", styleStore.appThemeModes);
    },
  },
});
